// «INSTALAR TEGO EN TU TELÉFONO» · DIRECTRIZ-PWA F1 (11 oct 2026, DISENO-F1 §9).
//
// Una pantalla que se explica sola, con el bloque que toca a este aparato:
// el botón de instalar (Android/Chrome con evento), «ya está instalada»,
// los tres pasos de Safari en iPhone/iPad, la guía por menú cuando el
// navegador no da botón, y en escritorio la dirección para abrirla desde
// el celular. Debajo, siempre, el estado: si la app quedó lista para abrir
// sin señal y si lo guardado está protegido. Las ayudas son datos
// (`pwa.*` en INTEGRADOR.toml), nunca cadenas sueltas.
//
// Vocabulario de cara al usuario: «instalar TEGO en tu teléfono», «sin
// señal», «guardado en este teléfono». Nada de siglas ni de nombres de
// mecanismos del navegador.
import { useState } from 'react';
import { Smartphone, CheckCircle2, Share, SquarePlus, Check, Loader2, Copy, MonitorSmartphone, CircleAlert } from 'lucide-react';
import Ayuda from '../../ui/Ayuda';
import CodigoQR from '../../ui/CodigoQR';
import { useInstalacion } from './useInstalacion';
import { esChromium, estaInstalada, esTactil, eventoDeInstalacion } from './instalacion';

const BOTON_PRIMARIO = 'w-full sm:w-auto min-h-12 px-5 rounded-xl bg-[var(--color-marca)] text-white text-base font-semibold '
  + 'hover:bg-[var(--color-marca-fuerte)] focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] focus:ring-offset-2';
const BOTON_SECUNDARIO = 'w-full sm:w-auto min-h-12 px-5 rounded-xl border border-gray-300 bg-white text-base font-medium text-gray-800 '
  + 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] focus:ring-offset-2';

function Tarjeta({ tono = 'neutra', children }) {
  const clases = tono === 'verde'
    ? 'border-green-200 bg-green-50 text-green-900'
    : 'border-gray-200 bg-white text-gray-800';
  return <section className={`rounded-xl border p-4 space-y-3 ${clases}`}>{children}</section>;
}

// ---------------------------------------------------------------------
// Los bloques por estado
// ---------------------------------------------------------------------
function BloqueInstalable({ instalar, enComputadora = false }) {
  const [resultado, setResultado] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  async function pulsar() {
    setOcupado(true);
    try { setResultado(await instalar()); } finally { setOcupado(false); }
  }
  return (
    <Tarjeta>
      <button type="button" onClick={pulsar} disabled={ocupado} aria-busy={ocupado}
              className={`${BOTON_PRIMARIO} ${ocupado ? 'opacity-70' : ''}`}>
        {enComputadora ? 'Instalar TEGO en esta computadora' : 'Instalar TEGO'}
      </button>
      <Ayuda id="pwa.instalar.boton" />
      {resultado === 'dismissed' && (
        <p role="status" className="text-sm text-gray-700">Cuando quieras, el botón sigue aquí.</p>
      )}
      {resultado === 'accepted' && (
        <p role="status" className="text-sm text-green-800">TEGO quedó instalada. Búscala en tu pantalla de inicio.</p>
      )}
    </Tarjeta>
  );
}

function BloqueInstalada() {
  return (
    <Tarjeta tono="verde">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="h-6 w-6 shrink-0 text-green-700" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <p className="text-base font-semibold">TEGO ya está instalada en este teléfono.</p>
          <p className="text-sm">Ábrela desde el icono «TEGO» de tu pantalla de inicio. Si la abriste desde el navegador, ciérralo y usa el icono.</p>
        </div>
      </div>
    </Tarjeta>
  );
}

function Paso({ icono: Icono, numero, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-marca-suave)] text-[var(--color-marca-oscura)]" aria-hidden="true">
        <Icono className="h-5 w-5" />
      </span>
      <div className="min-w-0 break-words text-base text-gray-800">
        <span className="sr-only">Paso {numero}. </span>
        {children}
      </div>
    </li>
  );
}

function BloqueIphone() {
  return (
    <Tarjeta>
      <p className="text-base font-semibold text-gray-900">En iPhone y iPad se instala desde Safari, en tres pasos:</p>
      <ol className="space-y-3">
        <Paso icono={Share} numero={1}>Toca el botón <strong>Compartir</strong> de Safari: el cuadro con la flecha hacia arriba, abajo en la pantalla.</Paso>
        <Paso icono={SquarePlus} numero={2}>Baja en la lista y toca <strong>Agregar a pantalla de inicio</strong>.</Paso>
        <Paso icono={Check} numero={3}>Toca <strong>Agregar</strong>, arriba a la derecha. El icono azul de TEGO aparece en tu inicio.</Paso>
      </ol>
      <p className="text-sm text-gray-700">Si TEGO se abrió desde otra app o desde otro navegador, ábrela en Safari para instalarla.</p>
      <Ayuda id="pwa.instalar.iphone" />
    </Tarjeta>
  );
}

function BloqueSinBoton({ recalcular }) {
  const [intento, setIntento] = useState(null);
  function comprobar() {
    recalcular();
    setIntento(estaInstalada() ? 'si' : 'no');
  }
  return (
    <Tarjeta>
      <p className="text-base font-semibold text-gray-900">Este navegador no muestra aquí el botón de instalar.</p>
      <p className="text-base text-gray-800">
        {esChromium()
          ? <>Toca el menú <strong>⋮</strong> (arriba a la derecha) y elige <strong>Instalar aplicación</strong> o <strong>Agregar a pantalla de inicio</strong>.</>
          : <>Busca <strong>Agregar a pantalla de inicio</strong> en el menú de tu navegador.</>}
      </p>
      <button type="button" onClick={comprobar} className={BOTON_SECUNDARIO}>Ya la instalé</button>
      {intento === 'no' && (
        <p role="status" className="text-sm text-gray-700">Todavía se abre desde el navegador. Ábrela desde el icono para terminar.</p>
      )}
    </Tarjeta>
  );
}

function BloqueEscritorio({ hayEvento, instalar }) {
  const direccion = `${window.location.origin}/app`;
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    try { await navigator.clipboard?.writeText(direccion); setCopiado(true); } catch { setCopiado(false); }
  }
  return (
    <Tarjeta>
      <div className="flex items-start gap-2">
        <MonitorSmartphone className="h-6 w-6 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
        <p className="text-base text-gray-800">TEGO se instala en el teléfono. Abre esta dirección en tu celular:</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 break-all rounded-lg bg-gray-100 px-3 py-2 text-base text-gray-900">{direccion}</code>
        <button type="button" onClick={copiar} className={`${BOTON_SECUNDARIO} inline-flex items-center justify-center gap-2`}>
          <Copy className="h-4 w-4" aria-hidden="true" />Copiar
        </button>
      </div>
      {copiado && <p role="status" className="text-sm text-green-800">Dirección copiada.</p>}
      <div className="hidden lg:block">
        <CodigoQR valor={direccion} tamano={144} etiqueta={`Código QR con la dirección ${direccion}`} />
      </div>
      {hayEvento && (
        <div className="pt-2">
          <BloqueInstalable instalar={instalar} enComputadora />
        </div>
      )}
    </Tarjeta>
  );
}

// ---------------------------------------------------------------------
// El bloque de estado, siempre visible
// ---------------------------------------------------------------------
function Punto({ tono }) {
  const color = tono === 'verde' ? 'bg-green-600' : tono === 'ambar' ? 'bg-amber-500' : 'bg-gray-400';
  return <span className={`mt-1.5 inline-block h-3 w-3 shrink-0 rounded-full ${color}`} aria-hidden="true" />;
}

function EstadoSinSenal({ worker, persistente }) {
  const listo = worker.estado === 'listo';
  const preparando = worker.estado === 'registrado' || worker.estado === 'pendiente';
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" aria-live="polite">
      {listo && (
        <div className="flex items-start gap-2"><Punto tono="verde" /><p className="text-base text-green-900">Lista para abrir sin señal.</p></div>
      )}
      {preparando && (
        <div className="flex items-start gap-2">
          <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-gray-500" aria-hidden="true" />
          <p className="text-base text-gray-700">Preparándose para abrir sin señal…</p>
        </div>
      )}
      {!listo && !preparando && (
        <div className="flex items-start gap-2">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <div className="min-w-0 space-y-1">
            <p className="text-base text-amber-900">TEGO no quedó lista para abrir sin señal.</p>
            <p className="text-sm text-gray-800">Qué revisar: que la dirección empiece con <code>https://</code> y no sea una IP; después recarga la página.</p>
            {worker.motivo && <p className="text-xs text-gray-500 break-words">Detalle para soporte: {worker.motivo}</p>}
          </div>
        </div>
      )}
      {persistente === true && (
        <div className="flex items-start gap-2"><Punto tono="verde" /><p className="text-base text-green-900">Lo que guardas sin señal está protegido: Sí.</p></div>
      )}
      {persistente === false && (
        <div className="flex items-start gap-2"><Punto tono="ambar" /><p className="text-base text-amber-900">Lo que guardas sin señal está protegido: No. Instala TEGO en tu teléfono para protegerlo.</p></div>
      )}
      <Ayuda id="pwa.instalar.guardado" />
    </section>
  );
}

// ---------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------
export default function InstalarTego() {
  const { estado, instalar, worker, persistente, recalcular } = useInstalacion();
  // CHROME DE ESCRITORIO CON EVENTO (auditoría F1, P2-2; DISENO §9.4): el
  // evento existe, pero la pantalla se titula «en tu teléfono». En una
  // computadora se enseña la dirección (y el QR) para el celular Y el botón
  // «Instalar TEGO en esta computadora», que dice dónde instala. El botón
  // «Instalar TEGO» a secas es solo para pantalla táctil.
  const enComputadora = estado === 'instalable' && !esTactil();
  return (
    <div className="mx-auto w-full max-w-prose space-y-4">
      <div className="flex items-start gap-3">
        <Smartphone className="h-7 w-7 shrink-0 text-[var(--color-marca)]" aria-hidden="true" />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-gray-900">Instalar TEGO en tu teléfono</h1>
          <p className="text-base text-gray-600">Con TEGO instalada la abres desde un icono, a pantalla completa, y sigue funcionando sin señal.</p>
        </div>
      </div>
      <Ayuda id="pwa.instalar.pantalla" />

      {estado === 'instalable' && !enComputadora && <BloqueInstalable instalar={instalar} />}
      {estado === 'instalada' && <BloqueInstalada />}
      {estado === 'iphone' && <BloqueIphone />}
      {estado === 'sin-boton' && <BloqueSinBoton recalcular={recalcular} />}
      {(estado === 'escritorio' || enComputadora) && (
        <BloqueEscritorio hayEvento={!!eventoDeInstalacion()} instalar={instalar} />
      )}

      <EstadoSinSenal worker={worker} persistente={persistente} />

      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-base font-medium text-gray-900 min-h-11 flex items-center">¿Qué funciona sin señal?</summary>
        <div className="mt-2 space-y-2">
          <Ayuda id="pwa.instalar.sin_senal" />
          <p className="text-sm text-gray-700">Para entrar a TEGO hace falta señal. Una vez dentro, la sesión sigue abierta sin ella.</p>
        </div>
      </details>
    </div>
  );
}
